import { z } from "zod";
import { storedTrustSchema } from "./knowledge.ts";
import { residencySchema } from "./models.ts";
import { messageIdSchema, projectIdSchema } from "./projects.ts";
import { entityIdSchema, userIdSchema } from "./tenancy.ts";

/**
 * Agent memory (openspec change `memory-personal`): personal memories the user confirmed, of
 * two kinds. A `preference` is about the user (tone, language, role, answer style) and applies
 * in all their conversations; a `fact` is about their work and keeps the project and flow label
 * of the answer it came from. The tenant and owner always come from the credential.
 */
export const memoryIdSchema = z.string().brand<"MemoryId">();
export const memoryProposalIdSchema = z.string().brand<"MemoryProposalId">();
export type MemoryId = z.infer<typeof memoryIdSchema>;
export type MemoryProposalId = z.infer<typeof memoryProposalIdSchema>;

export const memoryKindSchema = z.enum(["preference", "fact"]);
export type MemoryKind = z.infer<typeof memoryKindSchema>;

/** One plain-text statement: the server also rejects markup, links and source handles. */
export const MEMORY_TEXT_MAX_CHARS = 300;
export const memoryTextSchema = z.string().trim().min(1).max(MEMORY_TEXT_MAX_CHARS);

/** The project a fact came from; `name` is null when the caller can no longer read it. */
export const memoryOriginSchema = z.object({
  projectId: projectIdSchema,
  name: z.string().nullable(),
});
export type MemoryOrigin = z.infer<typeof memoryOriginSchema>;

/**
 * A memory as its owner sees it. A fact whose sources the owner can no longer read is
 * `hidden`: no text and no origin name, still deletable.
 */
export const memoryViewSchema = z.object({
  id: memoryIdSchema,
  kind: memoryKindSchema,
  /** Null when hidden. */
  text: z.string().nullable(),
  hidden: z.boolean(),
  /** Null for preferences and facts from standalone conversations. */
  origin: memoryOriginSchema.nullable(),
  /** `external`: drawn from an answer that used outside results; shown as unverified. */
  trust: storedTrustSchema,
  residency: residencySchema,
  /** Documents the fact's label references. */
  sourceCount: z.number().int().nonnegative(),
  createdAt: z.string(),
  updatedAt: z.string(),
  lastUsedAt: z.string().nullable(),
  /**
   * When the retention sweep will delete it, set by the list only once 80 % of the tenant's
   * `memory.retentionDays` have passed since its last use or edit; null before (#103). Absent
   * from older servers and from single-memory responses.
   */
  expiresAt: z.string().nullable().optional(),
  /**
   * The caller may suggest this fact to its origin project (memory-project): project memory is
   * on there and they hold `memory.propose`. Absent from servers before memory-project.
   */
  suggestable: z.boolean().optional(),
});
export type MemoryView = z.infer<typeof memoryViewSchema>;

/**
 * The caller's memories and whether personal memory is on at their default entity. `locked`:
 * an admin turned it off and locked it; memories can then be listed and deleted, not added.
 */
export const memoryListSchema = z.object({
  items: z.array(memoryViewSchema),
  enabled: z.boolean(),
  locked: z.boolean(),
});
export type MemoryList = z.infer<typeof memoryListSchema>;

export const memoryListQuerySchema = z.object({
  q: z.string().trim().max(200).optional(),
  kind: memoryKindSchema.optional(),
});
export type MemoryListQuery = z.infer<typeof memoryListQuerySchema>;

/** A memory written by hand: an empty label, residency `eu`. */
export const createMemoryRequestSchema = z.strictObject({
  kind: memoryKindSchema,
  text: memoryTextSchema,
});
export type CreateMemoryRequest = z.infer<typeof createMemoryRequestSchema>;

/** Editing keeps the label, origin and residency; a fact with sources stays a fact (`409`). */
export const updateMemoryRequestSchema = z
  .strictObject({
    text: memoryTextSchema.optional(),
    kind: memoryKindSchema.optional(),
  })
  .refine((body) => body.text !== undefined || body.kind !== undefined, {
    message: "Nothing to change",
  });
export type UpdateMemoryRequest = z.infer<typeof updateMemoryRequestSchema>;

/** Accepting a proposal, optionally with the user's edit of its text. */
export const acceptMemoryProposalRequestSchema = z.strictObject({
  text: memoryTextSchema.optional(),
});
export type AcceptMemoryProposalRequest = z.infer<typeof acceptMemoryProposalRequestSchema>;

/** A proposal under an answer, until accepted, dismissed or expired (7 days). */
export const memoryProposalViewSchema = z.object({
  id: memoryProposalIdSchema,
  messageId: messageIdSchema,
  kind: memoryKindSchema,
  text: z.string(),
  /** The user asked to remember it (their own words had a remember cue). */
  requested: z.boolean(),
  origin: memoryOriginSchema.nullable(),
  trust: storedTrustSchema,
  sourceCount: z.number().int().nonnegative(),
  expiresAt: z.string(),
  /**
   * The caller may send it to the project instead (memory-project): a fact of a project turn,
   * project memory is on there and they hold `memory.propose`.
   */
  suggestable: z.boolean().optional(),
  /**
   * The agent proposed it for the project (`propose_memory` with `scope: "project"`, agent-turn
   * D13): "Suggest for the project" is the card's primary action when `suggestable`.
   */
  toProject: z.boolean().optional(),
});
export type MemoryProposalView = z.infer<typeof memoryProposalViewSchema>;

/**
 * The proposal streamed after an answer, before `finish`; clients without memory ignore it. On
 * reload, pending proposals come with the conversation (`memoryProposals`).
 */
export const MEMORY_PROPOSAL_DATA_NAME = "memory-proposal";
export const MEMORY_PROPOSAL_PART = `data-${MEMORY_PROPOSAL_DATA_NAME}` as const;
export const MEMORY_PROPOSAL_PART_ID = "memory-proposal";
export const memoryProposalPartSchema = z.object({
  type: z.literal(MEMORY_PROPOSAL_PART),
  id: z.literal(MEMORY_PROPOSAL_PART_ID),
  data: memoryProposalViewSchema,
});
export type MemoryProposalPart = z.infer<typeof memoryProposalPartSchema>;

// ── Project memory (openspec change `memory-project`) ───────────────────────────────────────

/**
 * Who suggested or approved a project memory; shown to owners and managers only
 * (`memory.manage`), so members see the agreed fact, not who suggested it.
 */
export const memoryPersonSchema = z.object({ id: z.string(), name: z.string().nullable() });

export const projectMemoryStatusSchema = z.enum(["active", "pending"]);
export type ProjectMemoryStatus = z.infer<typeof projectMemoryStatusSchema>;

/**
 * A project memory as a member sees it. One whose sources the caller cannot read is `hidden`:
 * no text, and only a reject (pending) or delete (active) action for a manager.
 */
export const projectMemoryViewSchema = z.object({
  id: memoryIdSchema,
  status: projectMemoryStatusSchema,
  /** Null when hidden. */
  text: z.string().nullable(),
  hidden: z.boolean(),
  trust: storedTrustSchema,
  residency: residencySchema,
  sourceCount: z.number().int().nonnegative(),
  /** The caller suggested it (they may withdraw it while pending). */
  mine: z.boolean(),
  /** For `memory.manage` only; null for everyone else. */
  author: memoryPersonSchema.nullable(),
  decidedBy: memoryPersonSchema.nullable(),
  decidedAt: z.string().nullable(),
  /**
   * For `memory.manage`, on a memory with sources: how many of the project's members cannot
   * read them, so would never be given it. Null otherwise.
   */
  membersWithoutAccess: z.number().int().nonnegative().nullable(),
  /** When a pending memory expires; null once active. */
  expiresAt: z.string().nullable(),
  createdAt: z.string(),
  updatedAt: z.string(),
  lastUsedAt: z.string().nullable(),
});
export type ProjectMemoryView = z.infer<typeof projectMemoryViewSchema>;

export const projectMemoryListQuerySchema = z.object({
  status: projectMemoryStatusSchema.default("active"),
  q: z.string().trim().max(200).optional(),
});
export type ProjectMemoryListQuery = z.input<typeof projectMemoryListQuerySchema>;

/**
 * A project's memories in one status. `pendingCount`: the queue's size for `memory.manage`, the
 * caller's own pending suggestions otherwise. `enabled`: `memory.project` resolves on (when off,
 * memories stay listed and manageable but are neither used nor suggested).
 */
export const projectMemoryListSchema = z.object({
  items: z.array(projectMemoryViewSchema),
  pendingCount: z.number().int().nonnegative(),
  enabled: z.boolean(),
});
export type ProjectMemoryList = z.infer<typeof projectMemoryListSchema>;

/** A suggestion: one of the caller's pending fact proposals, or one of their own facts. */
export const suggestProjectMemoryRequestSchema = z.union([
  z.strictObject({ proposalId: memoryProposalIdSchema, text: memoryTextSchema.optional() }),
  z.strictObject({ memoryId: memoryIdSchema }),
]);
export type SuggestProjectMemoryRequest = z.infer<typeof suggestProjectMemoryRequestSchema>;

/** An owner's or manager's decision on a pending memory; `text` edits it before approval. */
export const decideProjectMemoryRequestSchema = z.discriminatedUnion("action", [
  z.strictObject({ action: z.literal("approve"), text: memoryTextSchema.optional() }),
  z.strictObject({ action: z.literal("reject") }),
]);
export type DecideProjectMemoryRequest = z.infer<typeof decideProjectMemoryRequestSchema>;

export const updateProjectMemoryRequestSchema = z.strictObject({ text: memoryTextSchema });
export type UpdateProjectMemoryRequest = z.infer<typeof updateProjectMemoryRequestSchema>;

// ── Governance (openspec change `memory-governance`) ────────────────────────────────────

/**
 * A memory in the owner's export (design §3): a fact whose sources the owner no longer reads is
 * listed without text, trust, residency or origin name (document access, ADR 0009, is not
 * overridden by the right to one's data).
 */
export const memoryExportItemSchema = z.discriminatedUnion("hidden", [
  z.object({
    id: memoryIdSchema,
    kind: memoryKindSchema,
    hidden: z.literal(false),
    text: z.string(),
    originProject: memoryOriginSchema.nullable(),
    trust: storedTrustSchema,
    residency: residencySchema,
    createdAt: z.string(),
    updatedAt: z.string(),
    lastUsedAt: z.string().nullable(),
  }),
  z.object({
    id: memoryIdSchema,
    kind: memoryKindSchema,
    hidden: z.literal(true),
    originProject: memoryOriginSchema.nullable(),
    createdAt: z.string(),
  }),
]);
export type MemoryExportItem = z.infer<typeof memoryExportItemSchema>;

/** One of the owner's own pending project suggestions, with its expiry. */
export const memoryExportSuggestionSchema = z.discriminatedUnion("hidden", [
  memoryExportItemSchema.options[0].extend({ expiresAt: z.string().nullable() }),
  memoryExportItemSchema.options[1].extend({ expiresAt: z.string().nullable() }),
]);
export type MemoryExportSuggestion = z.infer<typeof memoryExportSuggestionSchema>;

/** `GET /api/memories/export`: the caller's personal memories and pending suggestions. */
export const memoryExportSchema = z.object({
  exportedAt: z.string(),
  user: z.object({ id: userIdSchema, email: z.string(), name: z.string().nullable() }),
  /** Why some items are hidden: stated in the file itself. */
  notice: z.string(),
  memories: z.array(memoryExportItemSchema),
  pendingSuggestions: z.array(memoryExportSuggestionSchema),
});
export type MemoryExport = z.infer<typeof memoryExportSchema>;

/** The figures admins see (design §6): counts only, never a memory's text, kind or label. */
export const memoryMetricSchema = z.enum([
  "personal_memories",
  "users_with_memories",
  "proposals_accepted",
  "proposals_dismissed",
  "project_memories",
  "pending_suggestions",
]);
export type MemoryMetric = z.infer<typeof memoryMetricSchema>;

/** Entities with fewer people (by default entity) show no figures (k-anonymity). */
export const MEMORY_FIGURES_MIN_USERS = 5;

/**
 * `GET /api/admin/memory`: one row per entity in the admin's scope, root first. `figures` is
 * null when the entity has fewer than {@link MEMORY_FIGURES_MIN_USERS} people.
 */
export const memoryFiguresSchema = z.object({
  entities: z.array(
    z.object({
      entityId: entityIdSchema,
      name: z.string(),
      parentId: entityIdSchema.nullable(),
      figures: z.record(memoryMetricSchema, z.number().int().nonnegative()).nullable(),
    }),
  ),
});
export type MemoryFigures = z.infer<typeof memoryFiguresSchema>;

/** `GET /api/admin/users/{id}/memories/count`: a disabled user's personal memories. */
export const userMemoryCountSchema = z.object({ count: z.number().int().nonnegative() });
export type UserMemoryCount = z.infer<typeof userMemoryCountSchema>;

/** `POST /api/admin/users/{id}/memories/erase`: the count the admin confirmed. */
export const eraseUserMemoriesRequestSchema = z.strictObject({
  expected: z.number().int().nonnegative(),
});
export type EraseUserMemoriesRequest = z.infer<typeof eraseUserMemoriesRequestSchema>;

export const userMemoryErasureSchema = z.object({
  memories: z.number().int().nonnegative(),
  proposals: z.number().int().nonnegative(),
  /** Project memories the user authored: kept as project content, without an author. */
  projectMemoriesKept: z.number().int().nonnegative(),
});
export type UserMemoryErasure = z.infer<typeof userMemoryErasureSchema>;

/**
 * A governed project's active memory as a governing admin sees it (design §5): the text only
 * when the admin passes its label; otherwise `hidden`, without text.
 */
export const governedProjectMemorySchema = z.object({
  id: memoryIdSchema,
  text: z.string().nullable(),
  hidden: z.boolean(),
  trust: storedTrustSchema,
  sourceCount: z.number().int().nonnegative(),
  createdAt: z.string(),
  updatedAt: z.string(),
  lastUsedAt: z.string().nullable(),
});
export type GovernedProjectMemory = z.infer<typeof governedProjectMemorySchema>;

export const governedProjectMemoryListSchema = z.object({
  items: z.array(governedProjectMemorySchema),
});
export type GovernedProjectMemoryList = z.infer<typeof governedProjectMemoryListSchema>;
